/**
 * LA FILE DE RELECTURE DES EXERCICES — refonte de la console, lot L3 (maquette
 * `docs/pilotage/console-refonte/maquettes/03-revision-exercices.html`, retour
 * du fondateur du 15 octobre : « un espace de travail à deux volets qui tient
 * dans l'écran »).
 *
 * Tout ce que l'écran DÉCIDE vit ici, pour être éprouvé sans navigateur :
 * l'ordre de la file, ce qu'un filtre garde, la progression, où aller après
 * une décision, ce qu'une touche veut dire, et comment dire la bonne réponse
 * d'un corrigé. L'écran, lui, affiche et appelle.
 */
import {
  ecrireEnsembleExact,
  LIBELLES_CATEGORIE_EXERCICE,
  lireEnsembleExact,
  lireEnsemble,
  matriceEnLatex,
  type CorrigeExercice,
  type ExerciceRelectureResumeDto,
  type NiveauExercice,
  type StatutRelecture,
  type TableDeValeurs,
} from '@silex/shared';
import { pageDeLIndex, paginer, type PageDeListe } from './pagination';

/* ─────────────────────────── l'état d'un exercice ─────────────────────────── */

export type EtatFile = 'a_relire' | 'a_corriger' | 'relu';

/** « Relu » et « corrigé » : un humain l'a lu. « À corriger » : à part (D-1). */
export function etatDansLaFile(s: StatutRelecture): EtatFile {
  if (s === 'a_corriger') return 'a_corriger';
  if (s === 'relu' || s === 'corrige') return 'relu';
  return 'a_relire';
}

export const NOM_ETAT: Readonly<Record<EtatFile, string>> = {
  a_relire: 'À relire',
  a_corriger: 'À corriger',
  relu: 'Relu',
};

export const FILTRES_FILE = ['a_relire', 'a_corriger', 'relu', 'tous'] as const;
export type FiltreFile = (typeof FILTRES_FILE)[number];

export const NOM_FILTRE: Readonly<Record<FiltreFile, string>> = {
  a_relire: 'À relire',
  a_corriger: 'À corriger',
  relu: 'Relus',
  tous: 'Tous',
};

export const NOM_NIVEAU_EXERCICE: Readonly<Record<NiveauExercice, string>> = {
  1: 'Facile',
  2: 'Intermédiaire',
  3: 'Avancé',
  4: 'Situation-problème',
};

/** Une ligne de la file : le résumé du serveur, avec l'état tenu à jour après chaque geste. */
export type LigneFile = ExerciceRelectureResumeDto;

/** L'ordre de lecture : notion par notion, puis du plus simple au plus exigeant. */
export function ordonner(lignes: readonly LigneFile[]): LigneFile[] {
  return [...lignes].sort(
    (a, b) =>
      (a.notion.rang ?? 99) - (b.notion.rang ?? 99) ||
      a.niveauExercice - b.niveauExercice ||
      a.code.localeCompare(b.code),
  );
}

const plat = (t: string): string =>
  t
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

/** Ce qu'un filtre garde : état, notion, et une recherche dans le titre et l'énoncé. */
export function lignesVisibles(
  lignes: readonly LigneFile[],
  f: { readonly etat: FiltreFile; readonly notion: string | null; readonly recherche: string },
): LigneFile[] {
  const q = plat(f.recherche.trim());
  return lignes.filter(
    (l) =>
      (f.etat === 'tous' || etatDansLaFile(l.statutRelecture) === f.etat) &&
      (f.notion === null || l.notion.code === f.notion) &&
      (q === '' || plat(`${l.titre ?? ''} ${l.enonce}`).includes(q)),
  );
}

export interface Progression {
  readonly total: number;
  readonly relus: number;
  readonly aCorriger: number;
  readonly aRelire: number;
}

export function progression(lignes: readonly LigneFile[]): Progression {
  let relus = 0;
  let aCorriger = 0;
  for (const l of lignes) {
    const e = etatDansLaFile(l.statutRelecture);
    if (e === 'relu') relus += 1;
    else if (e === 'a_corriger') aCorriger += 1;
  }
  return { total: lignes.length, relus, aCorriger, aRelire: lignes.length - relus - aCorriger };
}

/** « 12 / 105 » : ce qui a été lu (relu ou à corriger) sur le total. */
export const direProgression = (p: Progression): string => `${p.relus + p.aCorriger} / ${p.total}`;

/** Les notions de la file, dans l'ordre, pour le filtre. */
export function notionsDeLaFile(
  lignes: readonly LigneFile[],
): { readonly code: string; readonly rang: number | null; readonly titre: string }[] {
  const vues = new Map<string, LigneFile['notion']>();
  for (const l of ordonner(lignes)) if (!vues.has(l.notion.code)) vues.set(l.notion.code, l.notion);
  return [...vues.values()];
}

/* ─────────────────────────── naviguer ─────────────────────────── */

/** Le suivant (+1) ou le précédent (−1) dans les lignes visibles ; reste en place aux bords. */
export function deplacer(
  visibles: readonly { readonly code: string }[],
  courant: string | null,
  sens: 1 | -1,
): string | null {
  if (visibles.length === 0) return null;
  const i = visibles.findIndex((l) => l.code === courant);
  if (i < 0) return visibles[0]!.code;
  return visibles[Math.min(Math.max(i + sens, 0), visibles.length - 1)]!.code;
}

/**
 * OÙ ALLER APRÈS UNE DÉCISION — « passage automatique au suivant ».
 *
 * La décision peut faire SORTIR l'exercice du filtre (« À relire » → relu) :
 * on ne peut donc pas prendre « l'indice + 1 » de la liste d'après. On prend,
 * dans la liste d'AVANT, le premier exercice qui suit le courant et qui est
 * encore visible ; à défaut, le dernier qui le précède ; à défaut, rien (le
 * filtre est vide : tout est fait).
 */
export function apresDecision(
  avant: readonly { readonly code: string }[],
  apres: readonly { readonly code: string }[],
  courant: string,
): string | null {
  const encore = new Set(apres.map((l) => l.code));
  const i = avant.findIndex((l) => l.code === courant);
  for (let k = i + 1; k < avant.length; k += 1) {
    const c = avant[k]!.code;
    if (c !== courant && encore.has(c)) return c;
  }
  for (let k = i - 1; k >= 0; k -= 1) {
    const c = avant[k]!.code;
    if (encore.has(c)) return c;
  }
  return encore.has(courant) ? courant : null;
}

/* ─────────────────────────── le clavier ─────────────────────────── */

export type ActionClavier = 'suivant' | 'precedent' | 'relu' | 'corriger';

/**
 * J / K : suivant / précédent ; R : relu ; C : à corriger. Jamais pendant une
 * saisie (la note, la recherche), jamais avec une touche de commande — Cmd+R
 * recharge la page, il ne marque rien.
 */
export function actionDeTouche(e: {
  readonly key: string;
  readonly ctrlKey?: boolean;
  readonly metaKey?: boolean;
  readonly altKey?: boolean;
  readonly dansUnChamp: boolean;
}): ActionClavier | null {
  if (e.dansUnChamp || e.ctrlKey || e.metaKey || e.altKey) return null;
  switch (e.key.toLowerCase()) {
    case 'j':
    case 'arrowdown':
      return 'suivant';
    case 'k':
    case 'arrowup':
      return 'precedent';
    case 'r':
      return 'relu';
    case 'c':
      return 'corriger';
    default:
      return null;
  }
}

/* ─────────────────────────── dire la bonne réponse ─────────────────────────── */

/** Une écriture du corrigé : LaTeX si elle en porte, du texte sinon. */
const enMaths = (e: string): string => (/[\\^_{}]/.test(e) ? `\\(${e}\\)` : e);
const nombreFr = (n: number): string => String(n).replace('.', ',');

export type BonneReponse =
  | { readonly genre: 'texte'; readonly texte: string; readonly details?: readonly string[] }
  | { readonly genre: 'option'; readonly id: string }
  | { readonly genre: 'table'; readonly table: TableDeValeurs; readonly cases: number }
  | {
      readonly genre: 'diagramme';
      readonly corrige: Extract<CorrigeExercice, { nature: 'diagramme' }>;
    }
  | { readonly genre: 'trace'; readonly texte: string };

/**
 * Ce que Silex attend, dit au relecteur, pour chacune des natures du corrigé.
 * Une nature inconnue (serveur plus récent) est DITE, jamais tue.
 */
export function bonneReponse(c: CorrigeExercice): BonneReponse {
  switch (c.nature) {
    case 'nombre':
      return {
        genre: 'texte',
        texte: `${nombreFr(c.valeur)}${c.unite ? ` ${c.unite}` : ''}`,
        details: c.tolerance !== undefined ? [`tolérance ± ${nombreFr(c.tolerance)}`] : undefined,
      };
    case 'expression':
      return {
        genre: 'texte',
        texte: `\\(${c.latex}\\)`,
        details:
          c.equivalents.length > 0
            ? [`acceptées aussi : ${c.equivalents.map((e) => `\\(${e}\\)`).join(' ; ')}`]
            : undefined,
      };
    case 'couple': {
      const [nx, ny] = c.noms ?? ['x', 'y'];
      return { genre: 'texte', texte: `${nx} = ${enMaths(c.x)} ; ${ny} = ${enMaths(c.y)}` };
    }
    case 'vecteur':
      /* Le vecteur attendu, nommé (tranche T43) : jamais lu comme un point. */
      return { genre: 'texte', texte: `\\(${c.nom} = (${c.x} ; ${c.y})\\)` };
    case 'intervalle':
      return { genre: 'texte', texte: enMaths(c.ensemble) };
    case 'ensemble_solution': {
      /* Tranche T45 : un ensemble en π, ou périodique — ses éléments en formules,
         « k ∈ ℤ » dit ; l'arrondi demandé en détail. */
      const exact =
        lireEnsemble(c.ensemble, c.variable) === null
          ? lireEnsembleExact(c.ensemble, c.variable)
          : null;
      if (exact === null)
        return { genre: 'texte', texte: `${c.variable} : ${enMaths(c.ensemble)}` };
      return {
        genre: 'texte',
        texte: `${c.variable} : ${ecrireEnsembleExact(exact, c.variable)}`,
        ...(c.arrondi !== undefined
          ? { details: [`Arrondi demandé : ${c.arrondi} décimale${c.arrondi > 1 ? 's' : ''}.`] }
          : {}),
      };
    }
    case 'choix':
      return { genre: 'option', id: c.bonne };
    case 'vrai_faux':
      return { genre: 'option', id: c.bonne ? 'vrai' : 'faux' };
    case 'demarche':
    case 'texte':
      return { genre: 'texte', texte: c.resultatAttendu };
    case 'table':
      return { genre: 'table', table: { lignes: c.lignes }, cases: c.aRemplir.length };
    case 'matrice':
      /* La matrice attendue, dessinée (tranche T41). */
      return { genre: 'texte', texte: matriceEnLatex(c) };
    case 'diagramme':
      return { genre: 'diagramme', corrige: c };
    case 'trace':
      return {
        genre: 'trace',
        texte: `Un tracé : ${c.solution.length} objet${c.solution.length > 1 ? 's' : ''} attendu${
          c.solution.length > 1 ? 's' : ''
        }, ${c.contraintes.length} contrainte${c.contraintes.length > 1 ? 's' : ''} vérifiée${
          c.contraintes.length > 1 ? 's' : ''
        }.`,
      };
    default: {
      const inconnue = (c as { readonly nature?: unknown }).nature;
      return {
        genre: 'texte',
        texte: `Nature de réponse « ${String(inconnue)} » : à lire dans la source.`,
      };
    }
  }
}

/** « Notion 3 · Avancé · Trouver l'erreur » — le chapeau d'une ligne. */
export function chapeau(l: Pick<LigneFile, 'notion' | 'niveauExercice' | 'categorie'>): string {
  const notion = l.notion.rang === null ? 'Prérequis' : `Notion ${l.notion.rang}`;
  return [notion, NOM_NIVEAU_EXERCICE[l.niveauExercice], LIBELLES_CATEGORIE_EXERCICE[l.categorie]]
    .filter(Boolean)
    .join(' · ');
}

/* ─────────────────────────── le chapitre honnête (L4) ─────────────────────────── */

/** « 64 exercices · 61 non relus · 1 à corriger » — le résumé de la section « Exercices ». */
export function resumeExercices(p: Progression | null): string {
  if (p === null) return 'lecture impossible';
  if (p.total === 0) return 'aucun exercice';
  const morceaux = [`${p.total} exercice${p.total > 1 ? 's' : ''}`];
  morceaux.push(p.aRelire === 0 ? 'tous lus' : `${p.aRelire} non relu${p.aRelire > 1 ? 's' : ''}`);
  if (p.aCorriger > 0) morceaux.push(`${p.aCorriger} à corriger`);
  return morceaux.join(' · ');
}

/**
 * La publication d'un chapitre dit ce qui reste à relire, EXERCICES COMPRIS :
 * « Publié · 105 contenus non relus, dont 64 exercices · 1 exercice à
 * corriger ». Exercices illisibles (`null`) : le compte des autres, et il le dit.
 */
export function resumePublication(
  statut: string,
  nonRelusSavoir: number,
  exercices: Progression | null,
): string {
  const ex = exercices?.aRelire ?? 0;
  const total = nonRelusSavoir + ex;
  const corps =
    total === 0
      ? 'tout est relu'
      : `${total} contenu${total > 1 ? 's' : ''} non relu${total > 1 ? 's' : ''}${
          ex > 0 ? `, dont ${ex} exercice${ex > 1 ? 's' : ''}` : ''
        }`;
  const corriger =
    exercices !== null && exercices.aCorriger > 0
      ? ` · ${exercices.aCorriger} exercice${exercices.aCorriger > 1 ? 's' : ''} à corriger`
      : '';
  const inconnu = exercices === null ? ' (exercices non comptés : lecture impossible)' : '';
  return `${statut} · ${corps}${corriger}${inconnu}`;
}

/* ─────────────────────────── le service (15 octobre 2026) ─────────────────────────── */

/**
 * RETIRER DU SERVICE, REMETTRE EN SERVICE — décision du fondateur du 15
 * octobre 2026. Réservé à l'ADMIN (le serveur refuse le tuteur, 403) : retirer
 * change ce que reçoivent les élèves. Un exercice ni publié ni retiré (validé,
 * brouillon) n'offre aucun des deux gestes : il se publie par son chapitre.
 */
export type GesteDeService = 'retirer' | 'remettre';

export function gesteDeService(role: string | undefined, statut: string): GesteDeService | null {
  if (role !== 'admin') return null;
  if (statut === 'publie') return 'retirer';
  if (statut === 'retire') return 'remettre';
  return null;
}

/** Le même plafond que le serveur (`MAX_CARACTERES_MOTIF_RETRAIT`). */
export const MAX_MOTIF_RETRAIT = 1000;

/** Un motif qu'on peut envoyer : non vide une fois rogné, et dans la borne. */
export function motifRetraitValide(motif: string): boolean {
  const m = motif.trim();
  return m !== '' && m.length <= MAX_MOTIF_RETRAIT;
}

/**
 * Les étapes du geste, DANS la page (jamais `confirm()`) : on ouvre, on écrit
 * le motif (retrait seulement), on confirme. Remettre en service n'a pas de
 * motif : on confirme directement.
 */
export type EtapeService = 'ferme' | 'motif' | 'confirmer';

export function ouvrirGeste(geste: GesteDeService): EtapeService {
  return geste === 'retirer' ? 'motif' : 'confirmer';
}

/** Du motif à la confirmation : seulement si le motif tient. */
export function continuerGeste(etape: EtapeService, motif: string): EtapeService {
  return etape === 'motif' && motifRetraitValide(motif) ? 'confirmer' : etape;
}

/** « 2 hors service » — les exercices d'un chapitre qui ne sont pas servis ; ou rien. */
export function direHorsService(c: {
  readonly total: number;
  readonly enService: number;
}): string | null {
  const n = c.total - c.enService;
  return n > 0 ? `${n} hors service` : null;
}

/**
 * LA PAGE DE LA FILE (AB#888) — par 20. L'exercice demandé (`ex`) l'emporte :
 * la page est celle où il tombe, si bien que J/K et le passage automatique
 * après une décision franchissent les pages d'eux-mêmes. Sans `ex` (ou un `ex`
 * hors du filtre), la page de l'adresse, ramenée dans les bornes, et le
 * courant en est le premier exercice.
 */
export function pageDeLaFile<T extends { readonly code: string }>(
  visibles: readonly T[],
  demande: string | null,
  pageAdresse: number,
): { readonly decoupe: PageDeListe<T>; readonly courant: string | null } {
  const indice = demande === null ? -1 : visibles.findIndex((l) => l.code === demande);
  const decoupe = paginer(visibles, indice >= 0 ? pageDeLIndex(indice) : pageAdresse);
  return { decoupe, courant: indice >= 0 ? demande : (decoupe.elements[0]?.code ?? null) };
}
